'use client'

import Image from 'next/image'
import { ButtonHTMLAttributes, forwardRef, useState } from 'react'
import { cn } from '../../utils'

type DropdownMenuProps = ButtonHTMLAttributes<HTMLButtonElement> & {
	label: string
	allItemLabel: string
	placeholder?: string
	filterName: string // 'industries' | 'types' | 'languages'
	filters: string[]
	items: { label: string; value: string }[]
	onSelection: (name: string, value: string) => void
}

export const FilterSelect = forwardRef<HTMLButtonElement, DropdownMenuProps>(
	(
		{
			children,
			className,
			placeholder = 'Select an option',
			label,
			allItemLabel,
			filterName,
			filters,
			items,
			onSelection,
			...rest
		},
		ref,
	) => {
		const [isOpen, setOpen] = useState(false)

		const toggle = (val: boolean) => {
			const overlayes: NodeListOf<HTMLDivElement> =
				document.querySelectorAll('.overlay-autoclose')
			if (val && overlayes.length)
				overlayes.forEach((elem) => {
					elem.click()
				})
			setOpen(val)
		}

		return (
			<>
				{isOpen && (
					<div
						onClick={() => setOpen(false)}
						className="overlay-autoclose fixed top-0 bottom-0 left-0 w-screen h-screen z-20"
					/>
				)}
				<div className={cn('max-md:w-full relative inline-block')}>
					<div className="relative inline-block w-full">
						<button
							ref={ref}
							id={'FilterSelect_' + filterName}
							onClick={() => toggle(!isOpen)}
							className={cn(
								'btn tertiary relative md flex justify-between w-full px-4 border border-gray-900 rounded-full',
								isOpen
									? '!bg-gray-50 !border-gray-50 !rounded-3xl !rounded-b-none z-40'
									: 'z-30',
								className,
							)}
							data-datocms-noindex="true"
							rel="nofollow"
							{...rest}
						>
							{isOpen ? (
								<span className="mr-1 text-gray-500">{placeholder}</span>
							) : (
								<span className="mr-1">{label}</span>
							)}
							{isOpen ? (
								<Image
									src="/images/top-black-carret.svg"
									width="12"
									height="12"
									alt="top carret"
								/>
							) : (
								<Image
									src="/images/down-black-carret.svg"
									width="12"
									height="12"
									alt="top carret"
								/>
							)}

							<div
								className={cn(
									'absolute left-0 top-11 w-full opacity-100 md:transition-opacity duration-50 z-30',
									isOpen ? '' : 'opacity-0',
								)}
								onClick={(e) => e.stopPropagation()}
							>
								{isOpen && (
									<ul className="pt-4 pb-1 px-4 bg-gray-50 border-t border-gray-150 rounded-b-3xl">
										<div
											className={
												items.length > 7
													? 'mb-1 max-h-64 overflow-y-auto scrollbar scrollbar-thumb-primary scrollbar-track-gray-100 scrollbar-w-0.5 scrollbar-thumb-rounded-full scrollbar-track-rounded-full'
													: ''
											}
										>
											<li className="flex items-center mb-4 gap-2">
												<input
													id={allItemLabel}
													type="checkbox"
													title={allItemLabel}
													name={allItemLabel}
													checked={filters.length === 0}
													onChange={() => onSelection(filterName, 'all')}
												/>
												<label htmlFor={allItemLabel}>{allItemLabel}</label>
											</li>
											{items.map((elem, index) => (
												<li
													key={elem.value}
													className="flex items-center mb-4 gap-2"
												>
													<div className="w-3 mr-2">
														<input
															id={elem.value}
															type="checkbox"
															title={elem.label}
															checked={filters.includes(elem.value)}
															onChange={() =>
																onSelection(filterName, elem.value)
															}
															name="types"
															tabIndex={index + 1}
														/>
													</div>
													<label
														htmlFor={elem.value}
														className="w-full text-left"
													>
														{elem.label}
													</label>
												</li>
											))}
										</div>
									</ul>
								)}
							</div>
						</button>
					</div>
				</div>
			</>
		)
	},
)

FilterSelect.displayName = 'FilterSelect'
